import React, { useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Flag from "react-flags";
import { Grid, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import styles from "features/settings/styles";
import CreateIcon from "@material-ui/icons/Create";
import { useHistory } from "react-router";
import { GetApp, Publish } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { useClientQueryWithErrorHandling, useMutationWithErrorHandling } from "hooks/errorHandling";
import { TRANSLATIONS_FILE } from "../queries";
import * as mutations from "../mutations";
import { convertBase64ToFile } from "utils/fileConverter";
import FileSaver from "file-saver";
import { getTenant } from "providers/TenantProvider";
import { getFlagName } from "utils/functions";

const useStyles = makeStyles(styles);

const TranslationComponent = (props) => {
  const { language } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const clientQuery = useClientQueryWithErrorHandling();
  const fileRef = useRef(null);
  const { countryCode } = getTenant();

  const [uploadFile] = useMutationWithErrorHandling(mutations.TRANSLATIONS_FILE);

  const handleEditTranslation = useCallback(() => {
    history.push({ pathname: `/settings/translations/${language.code}` });
  }, [language.code, history]);

  const handleExport = useCallback(async () => {
    const { data } = await clientQuery(TRANSLATIONS_FILE, {
      variables: { languageCode: language.code },
      fetchPolicy: "no-cache",
    });
    if (!data) return;

    const fileURL = convertBase64ToFile(data.translationsFile.content);
    FileSaver.saveAs(fileURL, data.translationsFile.name);
  }, [language.code, clientQuery]);

  const handleImport = useCallback(async () => {
    if (event.target.files.length <= 0) return false;
    await uploadFile({ variables: { languageCode: language.code, file: event.target.files[0] } });
  }, [language.code, uploadFile]);

  const openDialog = useCallback(() => {
    fileRef.current.value = "";
    fileRef.current.click();
  }, []);

  return (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Grid container alignItems="center">
          <Grid item>
            <Flag
              name={getFlagName(countryCode, language.culture.split("-")[1])}
              format="png"
              pngSize={32}
              shiny={true}
              basePath="/static/flags"
            />
          </Grid>
          <Grid item>
            <Typography className={classes.langSelectorText}>{language.shortName}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Tooltip title={t("General.Export")}>
          <IconButton onClick={handleExport}>
            <GetApp color="secondary" fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("General.Import")}>
          <IconButton onClick={openDialog}>
            <Publish color="secondary" fontSize="small" />
            <input id="file" type="file" ref={fileRef} style={{ display: "none" }} onChange={handleImport} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("General.Edit")}>
          <IconButton onClick={handleEditTranslation}>
            <CreateIcon color="secondary" fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

TranslationComponent.propTypes = {
  language: PropTypes.object,
};

export default TranslationComponent;
